import { useEffect, useState } from "react";
import { cloneItem, lineIdentity, toQuoteItem } from "../../utils/menu";
import { useAppStore, useCartStore } from "../../stores";
import { CartItem, Order } from "../../types/item";
import { useApp, useNavigate } from "../../hooks";
import APIs from "../../apis";

const unavailableError = (error: any) =>
  error?.code === "ITEM_UNAVAILABLE" || error?.code === "UNKNOWN_ITEM";

const unavailableMessage = (names: string[]) => {
  const label = names.join(", ");
  return `${label} ${names.length === 1 ? "is" : "are"} no longer available`;
};

export interface Props {
  orders: Order[];
  loading: boolean;
  reorder: (order: Order) => Promise<boolean>;
  trackOrder: (order: Order) => void;
}

const useViewModel = (): Props => {
  const items = useCartStore((state) => state.items);
  const setCart = useCartStore((state) => state.setCart);
  const promoCode = useCartStore((state) => state.promoCode);
  const location = useAppStore((state) => state.location);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const { navigate } = useNavigate();
  const { message } = useApp();

  useEffect(() => {
    getOrders();
  }, []);

  const getOrders = async () => {
    try {
      const { orders } = await APIs.orders.orders();
      setOrders(orders);
    } catch (error: any) {
      message.error(error?.message || "Could not load orders");
    } finally {
      setLoading(false);
    }
  };

  const quoteLine = (line: CartItem) =>
    APIs.cart.calculate({
      areaUuid: location?.area,
      branchUuid: location?.branch,
      promoCode,
      orderType: location?.orderType,
      cartSnapshot: { items: [toQuoteItem(line)] },
    });

  const applyLines = async (orderLines: CartItem[]) => {
    const next = [...items];
    for (const line of orderLines) {
      const index = next.findIndex(
        (existing) => lineIdentity(existing) === lineIdentity(line),
      );
      if (index < 0) next.push(line);
      else
        next[index] = {
          ...next[index],
          quantity: next[index].quantity + line.quantity,
        };
    }
    await setCart(next);
  };

  const reorder = async (order: Order) => {
    const lines = order.cartSnapshot?.items ?? [];
    if (!lines.length) {
      message.error("This order has nothing to add");
      return false;
    }

    const prepared = lines.map((item) =>
      cloneItem({ ...item, quantity: item.quantity ?? 1 }),
    );

    let blocked: any;
    try {
      await applyLines(prepared);
      message.success("Your order added to the cart");
      return true;
    } catch (error: any) {
      if (!unavailableError(error)) {
        message.error(error?.message || "Could not add this order to the cart");
        return false;
      }
      blocked = error;
    }

    const kept: CartItem[] = [];
    const skipped: string[] = [];
    for (const line of prepared) {
      try {
        await quoteLine(line);
        kept.push(line);
      } catch (error: any) {
        if (!unavailableError(error)) {
          message.error(
            error?.message || "Could not add this order to the cart",
          );
          return false;
        }
        skipped.push(line.name || "An item");
      }
    }

    if (!skipped.length) {
      message.error(blocked?.message || "Could not add this order to the cart");
      return false;
    }

    if (!kept.length) {
      message.error(unavailableMessage(skipped));
      return false;
    }

    try {
      await applyLines(kept);
    } catch (error: any) {
      message.error(error?.message || "Could not add this order to the cart");
      return false;
    }

    message.warning(`Added to cart. ${unavailableMessage(skipped)}`);
    return true;
  };

  return {
    orders,
    loading,
    reorder,
    trackOrder: (order: Order) => navigate(`/orders/${order.orderNo}`),
  };
};

export default useViewModel;
